import React, { FC, useEffect, useMemo } from 'react';
import {
    Button, TextField, Grid, FormControl,
    InputLabel, Select, MenuItem, FormHelperText,
    Dialog, DialogContent, DialogTitle, DialogActions,
    IconButton, Box, Checkbox, FormControlLabel, InputAdornment
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { PaperComponent } from 'shared/ui/draggable/Draggable';
import CloseIcon from '@mui/icons-material/Close';
import { nameValidation, normalizeString } from 'utils/Validations'
import { TipoAcumulacionEnum } from 'repository/timekeeper/@types/TypeAccrualEnum';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { createAccrualCodeSchema, FormValues, /* AccrualCodeSchemaType */ } from 'schemas/AccrualCode';
import { z } from 'zod';
import { DurationInput } from 'shared/ui/time/CustomTimeField';
import { AlertCustom } from 'shared/ui/alert/AlertCustom';
dayjs.extend(customParseFormat);

interface Props {
    open: boolean;
    setOpen: (open: boolean) => void;
    rows: any[];
    initialValues?: Partial<FormValues>;
    titleKey: string;
    onSubmit: (values: FormValues) => Promise<void> | void;
    readOnly?: boolean;
}


export const FormAccrualCode: FC<Props> = ({ open, setOpen, rows, initialValues, titleKey, onSubmit, readOnly = false }) => {
    const { t } = useTranslation()
    const { enqueueSnackbar } = useSnackbar();
    const [alerts, setAlerts] = React.useState(false);

    const defaultValues: FormValues = useMemo(() => ({
        name: initialValues?.name ?? '',
        abbreviation: initialValues?.abbreviation ?? '',
        type_accrual: initialValues?.type_accrual ?? TipoAcumulacionEnum.HORAS,
        hour_per_day:
            initialValues?.type_accrual === TipoAcumulacionEnum.DIAS && initialValues?.hour_per_day != null
                ? String(initialValues.hour_per_day)
                : initialValues?.hour_per_day ?? null,
        allow_manual_edits: initialValues?.allow_manual_edits ?? true,
        show_code: initialValues?.show_code ?? true,
    }), [initialValues]);

    const schema = useMemo(() => createAccrualCodeSchema({ t, rows, initialValues, readOnly }), [t, rows, initialValues, readOnly]);

    type SchemaType = z.infer<ReturnType<typeof createAccrualCodeSchema>>;

    const { control, register, reset, handleSubmit, watch, clearErrors, formState: { errors, isSubmitting, isDirty } } = useForm<SchemaType>({
        defaultValues,
        resolver: zodResolver(schema as any),
        mode: 'onChange',
    });

    const typeAccrual = watch('type_accrual');
    const nameWatch = watch('name');

    useEffect(() => {
        if (open) {
            reset(defaultValues);
        }
    }, [open, defaultValues, reset]);

    const changes = () => {
        if ( (isDirty || (nameWatch ?? '') !== '')) {
            setAlerts(true);
        } else {
            setOpen(false);
        }
    };

    const existName = (value: string) => {
        const normalizedInput = normalizeString(value);
        const currentId = initialValues?.id;
        const existsInRows = rows?.some((r: any) => {
            const sameName = normalizeString(r.name) === normalizedInput;
            const isSameRecord = currentId !== undefined && r?.id === currentId;
            return sameName && !isSameRecord;
        });
        return existsInRows;
    };

    const closeForm = () => {
        setOpen(false);
        setAlerts(false);
    }

    const onSubmitForm = handleSubmit(async (values) => {
        try {
            await onSubmit({
                ...values,
                hour_per_day: values.type_accrual === TipoAcumulacionEnum.DIAS ? values.hour_per_day : null,
            } as FormValues);
            closeForm();
        } catch (err) {
            console.error(err);
            enqueueSnackbar(t('general.errors.unexpected'), { variant: 'error' });
        }
    });

    return (
        <Dialog
            open={open}
            onClose={(event, reason) => {
                if (reason !== 'backdropClick') {
                    return;
                }
                changes()
            }}
            disableRestoreFocus
            fullWidth
            maxWidth="sm"
            PaperComponent={PaperComponent}
            aria-labelledby="draggable-dialog-title"
        >
            <DialogTitle style={{ cursor: 'move' }} id="draggable-dialog-title">
                {t(titleKey)}
            </DialogTitle>
            <IconButton
                aria-label="close"
                onClick={() => changes()}
                sx={{
                    position: 'absolute',
                    right: 8,
                    top: 8,
                    color: (theme) => theme.palette.grey[500],
                }}
            >
                <CloseIcon />
            </IconButton>
            <Box sx={{ top: 0, zIndex: 10, pb: 1 }}>
                {alerts && (
                    <AlertCustom alerts={alerts} setAlerts={setAlerts} closeForm={closeForm} t={t} />
                )}
            </Box>
            <DialogContent dividers>
                <Box sx={{ mb: 3 }}>
                    <Grid container spacing={2} sx={{ mt: 1, mb: 2 }}>
                        <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12 }}>
                            <TextField
                                fullWidth
                                label={t('accruals.accrual_codes.dialog.inputs.name')}
                                autoComplete='off'
                                variant='outlined'
                                {...register('name', {
                                    onChange: (e) => {
                                        if (!nameValidation(e.target.value)) {
                                            // prevent invalid chars by resetting value to previous valid state
                                            const current = watch('name');
                                            reset({ ...watch(), name: current });
                                        }
                                    },
                                })}
                                slotProps={{
                                    input: {
                                        endAdornment: <InputAdornment position="end">{`${watch('name').length}/100`}</InputAdornment>,
                                    },
                                    htmlInput: {
                                        maxLength: 100,
                                    }
                                }}
                                error={existName(nameWatch) || Boolean(errors.name)}
                                disabled={readOnly}
                                onBlur={() => clearErrors('name')}
                            />
                            <FormHelperText sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5, px: 1.75 }} component={'span'}>
                                <Box component={'span'} color={(theme) => theme.palette.error.main}>{errors.name?.message ?? (!readOnly && existName(nameWatch) && nameWatch !== '' ? t('accruals.accrual_codes.dialog.alerts.name_exist') : '')}</Box>
                            </FormHelperText>
                        </Grid>

                        <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12 }}>
                            <TextField
                                fullWidth
                                label={t('accruals.accrual_codes.dialog.inputs.abbreviation')}
                                autoComplete='off'
                                variant='outlined'
                                {...register('abbreviation')}
                                error={Boolean(errors.abbreviation)}
                                slotProps={{
                                    input: {
                                        endAdornment: <InputAdornment position="end">{`${watch('abbreviation').length}/3`}</InputAdornment>,
                                    },
                                    htmlInput: {
                                        maxLength: 3,
                                    }
                                }}
                                disabled={readOnly}
                                onBlur={() => clearErrors('abbreviation')}
                            />
                            <FormHelperText sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5, px: 1.75 }} component={'span'}>
                                <Box component={'span'} color={(theme) => theme.palette.error.main}>{errors.abbreviation?.message ?? ''}</Box>
                            </FormHelperText>
                        </Grid>

                        <Grid size={typeAccrual === TipoAcumulacionEnum.DIAS ? { xs: 6, sm: 6, md: 6, lg: 6 } : { xs: 12, sm: 12, md: 12, lg: 12 }}>
                            <FormControl fullWidth>
                                <InputLabel>{t('accruals.accrual_codes.dialog.inputs.type_accrual')}</InputLabel>
                                <Controller
                                    name="type_accrual"
                                    control={control}
                                    render={({ field }) => (
                                        <Select
                                            {...field}
                                            label={t('accruals.accrual_codes.dialog.inputs.type_accrual')}
                                            disabled={readOnly || initialValues?.id !== undefined}
                                        >
                                            <MenuItem value={TipoAcumulacionEnum.HORAS}>{t('accruals.accrual_codes.dialog.inputs.type_accrual_options.hours')}</MenuItem>
                                            <MenuItem value={TipoAcumulacionEnum.DIAS}>{t('accruals.accrual_codes.dialog.inputs.type_accrual_options.days')}</MenuItem>
                                            <MenuItem value={TipoAcumulacionEnum.DINERO}>{t('accruals.accrual_codes.dialog.inputs.type_accrual_options.money')}</MenuItem>
                                        </Select>
                                    )}
                                />
                            </FormControl>
                        </Grid>
                        {
                            typeAccrual === TipoAcumulacionEnum.DIAS && (
                                <Grid size={{ xs: 6, sm: 6, md: 6, lg: 6 }}>
                                    <Controller
                                        name="hour_per_day"
                                        control={control}
                                        render={({ field }) => (
                                            <DurationInput
                                                fullWidth
                                                label={t('accruals.accrual_codes.dialog.inputs.hours_per_days')}
                                                value={field.value ?? ''}
                                                onChange={(newValue) => {
                                                    if (!newValue) {
                                                        field.onChange(null);
                                                        return;
                                                    }
                                                    const formatted = newValue.raw;
                                                    field.onChange(formatted);
                                                }}
                                                error={Boolean(errors.hour_per_day)}
                                                helperText={errors.hour_per_day?.message ?? ''}
                                                disabled={readOnly || initialValues?.id !== undefined}
                                            />
                                        )}
                                    />
                                </Grid>
                            )
                        }

                        <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12 }}>
                            <Controller
                                name="allow_manual_edits"
                                control={control}
                                render={({ field }) => (
                                    <FormControlLabel control={<Checkbox checked={!!field.value} onChange={(e) => field.onChange(e.target.checked)} disabled={readOnly} />} label={t('accruals.accrual_codes.dialog.inputs.allow_manual_edits')} />
                                )}
                            />
                            <Controller
                                name="show_code"
                                control={control}
                                render={({ field }) => (
                                    <FormControlLabel control={<Checkbox checked={!!field.value} onChange={(e) => field.onChange(e.target.checked)} disabled={readOnly} />} label={t('accruals.accrual_codes.dialog.inputs.show_code')} />
                                )}
                            />
                        </Grid>
                    </Grid>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button variant="contained" color="error" onClick={() => changes()}>
                    {t('general.buttons.cancel')}
                </Button>
                {!readOnly && (
                    <Button variant="contained" color="primary" onClick={onSubmitForm} loading={isSubmitting}>
                        {t('general.buttons.save')}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
}
